import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const VisitorFeed = () => {
  const { user, userProfile } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVisitors = async () => {
      try {
        const instituteProfile = userProfile?.institute_profiles?.[0];
        const instituteId = instituteProfile?.id;
        if (!instituteId) return;

        // Get institute user_id for booth_visits query (booth_visits.institute_id = users.id)
        const { data: instUser } = await supabase
          ?.from('institute_profiles')
          ?.select('user_id')
          ?.eq('id', instituteId)
          ?.single();

        if (!instUser?.user_id) return;

        const { data, error } = await supabase
          ?.from('booth_visits')
          ?.select(`
            id,
            visit_duration_seconds,
            actions_taken,
            created_at,
            student:student_id(
              id, name, city, state,
              student_profiles!student_profiles_user_id_fkey(course_interest)
            )
          `)
          ?.eq('institute_id', instUser?.user_id)
          ?.not('student_id', 'is', null)
          ?.order('created_at', { ascending: false })
          ?.limit(10);

        if (error) throw error;

        const mapped = (data || [])?.map((v) => {
          const actions = Array.isArray(v?.actions_taken) ? v?.actions_taken : [];
          const durationSec = v?.visit_duration_seconds || 0;
          const mins = Math.floor(durationSec / 60);
          const secs = durationSec % 60;
          const timeOnBooth = durationSec > 0 ? `${mins}m ${secs}s` : 'Just arrived';

          let engagementLevel = 'low';
          if (actions?.length >= 3) engagementLevel = 'high';
          else if (actions?.length >= 1) engagementLevel = 'medium';

          const courseInterest = v?.student?.student_profiles?.[0]?.course_interest || 'General Inquiry';
          const location = [v?.student?.city, v?.student?.state]?.filter(Boolean)?.join(', ') || 'Unknown';

          // Determine if "active" = visited within last 30 minutes
          const visitedAt = new Date(v?.created_at);
          const isActive = (Date.now() - visitedAt?.getTime()) < 30 * 60 * 1000;

          return {
            id: v?.id,
            name: v?.student?.name || 'Anonymous',
            location,
            interest: courseInterest,
            engagementLevel,
            timeOnBooth,
            actions: actions?.length > 0 ? actions : ['Browsing'],
            isActive
          };
        });

        setVisitors(mapped);
      } catch (err) {
        console.error('Error loading visitors:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && userProfile) {
      loadVisitors();
    }
  }, [user?.id, userProfile]);

  const getEngagementColor = (level) => {
    switch (level) {
      case 'high': return 'bg-success text-success-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'low': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getEngagementLabel = (level) => {
    switch (level) {
      case 'high': return 'Hot Lead';
      case 'medium': return 'Warm Lead';
      case 'low': return 'Cold Lead';
      default: return 'Browsing';
    }
  };

  const activeCount = visitors?.filter((v) => v?.isActive)?.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Recent Visitors
              </h2>
              <p className="text-sm text-muted-foreground">
                <span className="data-text">{activeCount}</span> active in last 30 min
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-sm font-medium text-success">Live</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading visitors...</p>
        </div>
      ) : visitors?.length === 0 ? (
        <div className="p-8 text-center">
          <Icon name="Users" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No visitors yet. Share your booth link to attract students!</p>
        </div>
      ) : (
        <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
          {visitors?.map((visitor) => (
            <div key={visitor?.id} className="p-4 md:p-6 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-lg font-bold text-primary">
                      {visitor?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  {visitor?.isActive && (
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success border-2 border-card rounded-full" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{visitor?.name}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span className="truncate">{visitor?.location}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getEngagementColor(visitor?.engagementLevel)}`}>
                      {getEngagementLabel(visitor?.engagementLevel)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <Icon name="GraduationCap" size={14} color="var(--color-primary)" />
                    <span className="text-foreground line-clamp-1">{visitor?.interest}</span>
                  </div>

                  <div className="flex items-center gap-4 mb-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={14} />
                      <span className="data-text">{visitor?.timeOnBooth}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Activity" size={14} />
                      <span className="data-text">{visitor?.actions?.length} actions</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {visitor?.actions?.map((action, index) => (
                      <span key={index} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                        {action}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="default" size="sm" iconName="MessageSquare" iconPosition="left">
                      Start Chat
                    </Button>
                    <Button variant="outline" size="sm" iconName="Video" iconPosition="left">
                      Video Call
                    </Button>
                    <Button variant="ghost" size="sm" iconName="UserPlus" iconPosition="left">
                      Add to Leads
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VisitorFeed;